import apiService from './apiService';
import { authEvents, isMfaRequired, reportUnauthorized } from '../utils/authEvents';
import { logger } from '../utils/logger';
import { getActiveTimezone } from '../utils/timezone';
import { getAuthToken } from '../utils/authToken';
import { httpError } from '../utils/userError';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

// #446 Phantom turns: in-flight cancel requests, keyed by `<conversation>:<user message>`.
// A new stream for the SAME message (edit-and-resend) waits for its predecessor's
// cancel to land first, so a timestamp-only cancel (Stop pressed before the run id
// arrived) can never be recorded AFTER the new run started and cancel it instead.
const pendingRunCancels = new Map();

// #448 re-open: true once the page is being reloaded / closed. A reader that
// dies because the page went away must never cancel the run — the server
// finishes it and saves the reply (#457); only an explicit Stop cancels.
let pageUnloading = false;
if (typeof window !== 'undefined' && window.addEventListener) {
    const markUnloading = () => { pageUnloading = true; };
    // `pagehide`, not `beforeunload`: the latter also fires when a "Leave
    // site?" prompt is then cancelled, which would disarm Stop for good.
    window.addEventListener('pagehide', markUnloading);
    // A page restored from the back/forward cache is alive again.
    window.addEventListener('pageshow', () => { pageUnloading = false; });
}
/** Test seam: whether an unload is in progress. */
export const isPageUnloading = () => pageUnloading;

// Helper to handle 401 responses
const handleAuthError = (response) => {
    if (response.status === 401) {
        // `mfa_required` (#333) → immediate sign-out; else refresh-then-sign-out.
        reportUnauthorized(response);
        throw new Error('Authentication required. Please log in again.');
    }
};

// XHR upload helper — fetch() can't expose upload-byte progress, so we drop
// down to XMLHttpRequest specifically so the header progress ring can reflect
// real bytes sent (not estimated time).
const xhrUpload = (url, formData, { onByteProgress = null, fallback = 'Upload failed', signal = null } = {}) => {
    return new Promise((resolve, reject) => {
        // Allow cancelling a hung/accidental upload (Cancel Upload ticket): an
        // already-aborted signal short-circuits, otherwise wire abort → xhr.abort().
        if (signal?.aborted) {
            const err = new Error('Upload aborted');
            err.aborted = true;
            reject(err);
            return;
        }
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.setRequestHeader('Authorization', `Bearer ${getAuthToken()}`);

        if (signal) {
            signal.addEventListener('abort', () => xhr.abort(), { once: true });
        }

        if (onByteProgress) {
            xhr.upload.onprogress = (evt) => {
                if (!evt.lengthComputable) return;
                const pct = evt.total > 0 ? Math.round((evt.loaded / evt.total) * 100) : 0;
                try { onByteProgress({ loaded: evt.loaded, total: evt.total, percent: pct }); } catch (_) { /* ignore */ }
            };
        }

        xhr.onload = () => {
            if (xhr.status === 401) {
                // Soft-handle: fire the auth-expired event so the App-level
                // listener can attempt a token refresh first. We DO NOT
                // unilaterally wipe localStorage or redirect — sleep/wake
                // cycles commonly hit one 401 before the refresh lands;
                // killing the session here regresses the persistence bug.
                // A `mfa_required` 401 (#333) is the exception: sign out now.
                let body401 = null;
                try { body401 = JSON.parse(xhr.responseText || 'null'); } catch (_) { /* not JSON */ }
                if (isMfaRequired(body401)) authEvents.emitMfaRequired();
                else authEvents.emitAuthExpired();
                reject(new Error('Authentication required. Please retry.'));
                return;
            }
            if (xhr.status >= 200 && xhr.status < 300) {
                try { resolve(JSON.parse(xhr.responseText || '{}')); }
                catch (_) { resolve({}); }
                return;
            }
            let detail = '';
            let errType = '';
            let parsedBody = null;
            try {
                parsedBody = JSON.parse(xhr.responseText || '{}');
                detail = parsedBody?.error?.message || parsedBody?.message || parsedBody?.detail || '';
                errType = parsedBody?.error?.type || '';
            } catch (_) {
                detail = (xhr.responseText || '').slice(0, 400);
            }
            // Surface the HTTP status + error type so callers can special-case a
            // duplicate-filename collision (409) and offer a rename instead of just
            // showing a failure.
            const err = new Error(`${detail || fallback} (HTTP ${xhr.status})`);
            err.status = xhr.status;
            err.code = errType;
            err.detail = detail;
            err.existingDocumentId = parsedBody?.existing_document_id || null;
            err.body = parsedBody;
            err.ref = parsedBody?.error?.ref || null;
            reject(err);
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.onabort = () => reject(new Error('Upload aborted'));
        xhr.send(formData);
    });
};

export const documentService = {
    /**
     * Upload a document (always uploaded to user's own collection)
     * @param {File} file - The file to upload
     * @param {function} onProgress - Optional progress callback for archives
     * @param {function} onByteProgress - Optional ({loaded,total,percent}) callback for body progress
     * @returns {Promise} Upload response
     */
    async uploadDocument(file, onProgress = null, onByteProgress = null) {
        const formData = new FormData();
        formData.append('file', file);

        try {
            const result = await xhrUpload(`${API_BASE_URL}/api/documents/upload`, formData, {
                onByteProgress,
            });

            // Handle archive extraction - upload files one by one
            if (result.type === 'archive_extracted' && onProgress) {
                return await this._uploadExtractedFiles(result, onProgress);
            }

            return result;
        } catch (error) {
            console.error('Document upload error:', error);
            throw error;
        }
    },

    /**
     * Upload extracted files from archive one by one
     * @private
     */
    async _uploadExtractedFiles(extractionResult, onProgress) {
        return this._uploadExtractedFilesToFolder(extractionResult, null, onProgress);
    },

    /**
     * Search documents (searches user's own documents only)
     * @param {string} query - Search query
     * @param {object} options - Search options
     * @returns {Promise} Search results
     */
    async searchDocuments(query, options = {}) {
        const searchRequest = {
            query,
            limit: options.limit || 5
        };

        return apiService.post('/api/documents/search', searchRequest);
    },

    /**
     * Get streaming RAG-enhanced response (uses user's own documents only)
     * @param {string} query - User query
     * @param {object} options - Query options
     * @param {function} onToken - Callback for each token
     * @param {function} onComplete - Callback for completion
     * @param {function} onError - Callback for errors
     * @param {function} onStatus - Callback for status updates
     * @param {AbortSignal} signal - Optional AbortSignal for cancellation
     * @returns {function} Stop function
     */
    async ragQueryStream(query, options = {}, onToken, onComplete, onError, onStatus = null, signal = null) {
        logger.info('RAG QUERY', 'Starting with query:', query);
        // Tell the backend (and the model) what time it is for the user, so
        // "remind me in 20 mins" / "schedule for 3pm" resolve to the user's
        // local timezone instead of the server's UTC.
        const tzName = (() => {
            try {
                return getActiveTimezone() || null; // user's reference tz, else browser (#125)
            } catch { return null; }
        })();

        const request = {
            query,
            model_name: options.model_name,
            conversation_context: options.conversation_context || [],
            conversation_id: options.conversation_id || null,
            max_documents: options.max_documents,
            replied_to_messages: options.replied_to_messages || null,
            attached_documents: options.attached_documents || null,
            referenced_expenses: options.referenced_expenses || null,
            client_timezone: tzName,
            client_now: new Date().toISOString(),  // UTC ISO with Z
            // Hybrid composition (spec §3): when the user's message combines
            // typed text with voice segments, hand the BE the two sources as
            // discrete fields. The BE rewrites the LLM prompt with explicit
            // "Typed text / Voice transcriptions" headers so the model can
            // acknowledge both ("I've analyzed the text you provided and,
            // as you requested in your audio, …"). Either field may be null.
            text_content: options.text_content || null,
            audio_transcriptions: options.audio_transcriptions || null,
            // #446: the stored USER message this run answers. Lets Stop reach the
            // server (POST …/messages/<id>/cancel-run) — on any instance.
            user_message_id: options.user_message_id || null,
            // #448 re-open: the automatic re-send of a turn that never started
            // (reload inside the save → run gap). The server refuses to start a
            // second run when one already started for this message.
            resend_only_if_not_started: !!options.resend_only_if_not_started,
        };
        logger.info('RAG QUERY', 'Request object:', request);

        // #446: run identity + server-side cancellation. `runId` arrives in the
        // first status chunk; until then a cancel is a timestamp the backend
        // compares against the run's start.
        const cancelKey = (request.conversation_id && request.user_message_id)
            ? `${request.conversation_id}:${request.user_message_id}`
            : null;
        let runId = null;
        let runFinished = false;
        let cancelPromise = null;
        const fireCancel = () => {
            // #448 re-open: a page that is being reloaded / closed must NOT
            // cancel the run — the server finishes it and saves the reply
            // itself (#457). Only an explicit user Stop cancels.
            if (pageUnloading) return cancelPromise;
            if (!cancelKey || runFinished || cancelPromise) return cancelPromise;
            cancelPromise = apiService
                .cancelRun(request.conversation_id, request.user_message_id, runId)
                .catch((e) => { logger.warn('RAG QUERY', 'cancel-run failed:', e); return null; })
                .finally(() => {
                    if (pendingRunCancels.get(cancelKey) === cancelPromise) pendingRunCancels.delete(cancelKey);
                });
            pendingRunCancels.set(cancelKey, cancelPromise);
            return cancelPromise;
        };
        if (cancelKey && pendingRunCancels.has(cancelKey)) {
            try { await pendingRunCancels.get(cancelKey); } catch (_) { /* fail-open */ }
        }
        if (signal) {
            if (signal.aborted) return () => {};
            signal.addEventListener('abort', fireCancel, { once: true });
        }

        try {
            logger.info('RAG QUERY', 'Making fetch request to:', `${API_BASE_URL}/api/documents/rag-query-stream`);
            const response = await fetch(`${API_BASE_URL}/api/documents/rag-query-stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getAuthToken()}`
                },
                body: JSON.stringify(request),
                signal: signal
            });

            logger.info('RAG QUERY', 'Fetch response status:', response.status);
            handleAuthError(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                logger.error('RAG QUERY', 'Fetch error:', response.status, errorData);
                // #439: status + body ride the Error so the chat can tell a
                // functional refusal (402 energy_empty, 403 a gate) from a failure.
                throw httpError(response.status, errorData);
            }

            logger.info('RAG QUERY', 'Starting stream reader');
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let stopped = false;
            let metadata = null;

            // `cancelServer: false` (#457) lets the caller drop its reader
            // WITHOUT stopping the run on the server — the silence watchdog uses
            // it: the server keeps going and saves the reply itself.
            const stopFunction = (opts = {}) => {
                stopped = true;
                if (opts.cancelServer !== false) fireCancel();
                reader.cancel();
            };

            // Process the stream
            const processStream = async () => {
                let completionCalled = false;
                // F-5.2 #1: track whether we saw a clean terminator ([DONE] or a
                // finish_reason chunk). If the reader ends (done) WITHOUT one, the
                // connection dropped mid-turn and the accumulated text is truncated —
                // we must NOT save it as a clean answer (see the no-DONE branch below).
                let sawTerminator = false;
                // F-5.2 #2: deferred card events (duplicate_warning, calendar_event_created,
                // …) are attached to `metadata`, which only exists once the metadata chunk
                // arrives. If one arrives BEFORE that chunk it would be silently dropped —
                // buffer them here and flush onto metadata the moment it is created.
                const pendingDeferred = [];
                // Error Clean up #439: the backend's error chunk (finish_reason
                // 'error'). Its text used to be appended to the answer like any
                // token, which is how "504 DEADLINE_EXCEEDED {…}" became a
                // normal-looking reply. It is now carried SEPARATELY on the
                // completion metadata (`stream_error`) so the chat renders an
                // error state — the text already delivered stays (#328).
                let streamError = null;
                // #457: the server saved the assistant message itself and told us
                // its id in the last chunk before [DONE]. MainOfficeView adopts
                // that id instead of POSTing a second copy.
                let savedByServer = null;
                // #448 re-open: an automatic re-send found a run already started
                // for this message — this stream answers nothing.
                let runAlreadyStarted = false;
                const withStreamError = (md) => {
                    let out = streamError
                        ? { ...(md || {}), stream_error: streamError }
                        : md;
                    if (runAlreadyStarted) out = { ...(out || {}), run_already_started: true };
                    if (savedByServer) {
                        out = {
                            ...(out || {}),
                            assistant_message_id: savedByServer.id,
                            server_stopped: !!savedByServer.stopped,
                        };
                    }
                    return out;
                };
                // Run `mutate` against metadata now if it exists, otherwise buffer it (and warn)
                // to replay once the metadata chunk arrives. The thunk closes over the `metadata`
                // `let`, so on replay it sees the freshly-created object.
                const withMetadata = (eventType, mutate) => {
                    if (metadata) { mutate(); return; }
                    logger.warn('RAG QUERY', `Deferred event "${eventType}" arrived before metadata chunk — buffering to replay`);
                    pendingDeferred.push(mutate);
                };
                try {
                    while (!stopped) {
                        const { done, value } = await reader.read();

                        if (done) break;

                        buffer += decoder.decode(value, { stream: true });

                        // Process complete lines
                        const lines = buffer.split('\n');
                        buffer = lines.pop() || ''; // Keep incomplete line in buffer

                        for (const line of lines) {
                            if (line.trim() === '') continue;

                            if (line.startsWith('data: ')) {
                                const data = line.slice(6); // Remove 'data: '

                                if (data === '[DONE]') {
                                    logger.info('RAG QUERY', 'Stream received [DONE], calling onComplete with metadata:', metadata);
                                    runFinished = true;
                                    completionCalled = true;
                                    sawTerminator = true;
                                    try { await reader.cancel(); } catch (e) { /* ignore */ }
                                    if (onComplete) onComplete(withStreamError(metadata));
                                    return;
                                }

                                try {
                                    const parsed = JSON.parse(data);

                                    if (parsed.type === 'run_already_started') {
                                        runAlreadyStarted = true;
                                        continue;
                                    }

                                    // #457: the server persisted the assistant message.
                                    if (parsed.type === 'assistant_message_saved') {
                                        savedByServer = {
                                            id: parsed.assistant_message_id || null,
                                            stopped: !!parsed.stopped,
                                        };
                                        continue;
                                    }

                                    // Handle status updates (legacy generic status)
                                    if (parsed.type === 'status') {
                                        logger.info('RAG STATUS', `${parsed.step}: ${parsed.message}`);
                                        if (parsed.run_id && !runId) {
                                            runId = parsed.run_id;
                                            if (options.onRunId) {
                                                try { options.onRunId(runId); } catch (_) { /* ignore */ }
                                            }
                                        }
                                        if (onStatus) {
                                            onStatus(parsed.message, parsed.step);
                                        }
                                        continue;
                                    }

                                    // Handle structured tool status events (real-time tool lifecycle)
                                    if (parsed.type === 'tool_status') {
                                        logger.info('RAG TOOL', `${parsed.event}: ${parsed.tool} - ${parsed.args_summary || parsed.result_summary}`);
                                        if (onStatus) {
                                            onStatus(parsed, parsed.event);
                                        }
                                        continue;
                                    }

                                    // Handle grounding result events
                                    if (parsed.type === 'grounding_result') {
                                        withMetadata('grounding_result', () => {
                                            metadata.grounding_used = parsed.grounding_used || false;
                                            metadata.grounding_search_count = parsed.grounding_search_count || 0;
                                        });
                                        continue;
                                    }

                                    // Handle document edit completion events.
                                    // Extra Documents #187: a single turn can edit MANY
                                    // documents (the BE emits one document_edit_complete per
                                    // edit_document tool call). Accumulate them in an ARRAY so
                                    // every edit renders its own card — the old single field
                                    // was overwritten by the last event, hiding all the rest.
                                    // The scalar `document_edit_complete` is kept (= the FIRST
                                    // edit) for backward-compat with older persisted messages.
                                    if (parsed.type === 'document_edit_complete') {
                                        logger.info('RAG EDIT', 'Document edit complete:', parsed);
                                        withMetadata('document_edit_complete', () => {
                                            (metadata.document_edits = metadata.document_edits || []).push(parsed);
                                            if (!metadata.document_edit_complete) metadata.document_edit_complete = parsed;
                                        });
                                        continue;
                                    }

                                    // Handle document create completion events (see #187 above:
                                    // one card PER created document, accumulated in an array).
                                    if (parsed.type === 'document_create_complete') {
                                        logger.info('RAG CREATE', 'Document create complete:', parsed);
                                        withMetadata('document_create_complete', () => {
                                            (metadata.document_creates = metadata.document_creates || []).push(parsed);
                                            if (!metadata.document_create_complete) metadata.document_create_complete = parsed;
                                        });
                                        continue;
                                    }

                                    // delete_document asked for confirmation — attach the
                                    // request so the message renders a review card (filename
                                    // + Preview + Download) before the user says yes.
                                    if (parsed.type === 'document_delete_request') {
                                        logger.info('RAG DELETE', 'Document delete request:', parsed);
                                        withMetadata('document_delete_request', () => {
                                            metadata.document_delete_request = parsed;
                                        });
                                        continue;
                                    }

                                    // delete_calendar_events (batch) asked for confirmation —
                                    // attach the request so the message renders a batch
                                    // event-deletion card (Batch event deletion #84).
                                    if (parsed.type === 'calendar_events_delete_request') {
                                        logger.info('RAG DELETE', 'Calendar events (batch) delete request:', parsed);
                                        withMetadata('calendar_events_delete_request', () => {
                                            metadata.calendar_events_delete_request = parsed;
                                        });
                                        continue;
                                    }

                                    // update_tickets (batch) asked for confirmation — attach the
                                    // request so the message renders a batch ticket-update card
                                    // (Mass deportations / Batch Ticket Operations #89).
                                    if (parsed.type === 'ticket_batch_update_request') {
                                        logger.info('RAG BOARD', 'Ticket batch update request:', parsed);
                                        withMetadata('ticket_batch_update_request', () => {
                                            metadata.ticket_batch_update_request = parsed;
                                        });
                                        continue;
                                    }

                                    // delete_tickets (batch) asked for confirmation — attach the
                                    // request so the message renders a batch ticket-deletion card
                                    // with previews (Selective elimination #93).
                                    if (parsed.type === 'ticket_batch_delete_request') {
                                        logger.info('RAG BOARD', 'Ticket batch delete request:', parsed);
                                        withMetadata('ticket_batch_delete_request', () => {
                                            metadata.ticket_batch_delete_request = parsed;
                                        });
                                        continue;
                                    }

                                    // delete_boards (batch) asked for confirmation — attach the
                                    // request so the message renders a board-deletion card with
                                    // per-board counts + Open Board deep links (PAI Board Management #102).
                                    if (parsed.type === 'board_batch_delete_request') {
                                        logger.info('RAG BOARD', 'Board batch delete request:', parsed);
                                        withMetadata('board_batch_delete_request', () => {
                                            metadata.board_batch_delete_request = parsed;
                                        });
                                        continue;
                                    }

                                    // leave_board asked for confirmation — attach the request so
                                    // the message renders the mandatory exit card (#275).
                                    if (parsed.type === 'board_leave_request') {
                                        logger.info('RAG BOARD', 'Board leave request:', parsed);
                                        withMetadata('board_leave_request', () => {
                                            metadata.board_leave_request = parsed;
                                        });
                                        continue;
                                    }

                                    // rename_documents asked for confirmation — attach the request
                                    // so the message renders a rename card (PAI renames documents #119).
                                    if (parsed.type === 'document_rename_request') {
                                        logger.info('RAG DOC', 'Document rename request:', parsed);
                                        withMetadata('document_rename_request', () => {
                                            metadata.document_rename_request = parsed;
                                        });
                                        continue;
                                    }

                                    // attach_documents_to_ticket asked for confirmation — attach the
                                    // request so the message renders a doc-attach verification card
                                    // (PAI Document Attachment Boards #118).
                                    if (parsed.type === 'ticket_attach_request') {
                                        logger.info('RAG BOARD', 'Ticket attach request:', parsed);
                                        withMetadata('ticket_attach_request', () => {
                                            metadata.ticket_attach_request = parsed;
                                        });
                                        continue;
                                    }

                                    // move_column asked for confirmation — attach the request so the
                                    // message renders a column-move confirmation card (PAI column move #111).
                                    if (parsed.type === 'column_move_request') {
                                        logger.info('RAG BOARD', 'Column move request:', parsed);
                                        withMetadata('column_move_request', () => {
                                            metadata.column_move_request = parsed;
                                        });
                                        continue;
                                    }

                                    // delete_board_label asked for confirmation — attach the
                                    // request so the message renders a label-deletion card (label
                                    // chip + affected-ticket list + Confirm/Cancel) before the
                                    // label is stripped from the palette + tagged tickets
                                    // (Label Management #212).
                                    if (parsed.type === 'label_delete_request') {
                                        logger.info('RAG BOARD', 'Label delete request:', parsed);
                                        withMetadata('label_delete_request', () => {
                                            metadata.label_delete_request = parsed;
                                        });
                                        continue;
                                    }

                                    // delete_column asked for confirmation — attach the request
                                    // so the message renders a column-deletion card (column name +
                                    // board + affected-ticket list + Confirm/Cancel) before the
                                    // column AND its tickets are deleted (Column Management #204).
                                    if (parsed.type === 'column_delete_request') {
                                        logger.info('RAG BOARD', 'Column delete request:', parsed);
                                        withMetadata('column_delete_request', () => {
                                            metadata.column_delete_request = parsed;
                                        });
                                        continue;
                                    }

                                    // delete_calendar_event asked for confirmation — attach the
                                    // request so the message renders an event-deletion card
                                    // (Confirmation Card #83) before anything is deleted.
                                    if (parsed.type === 'calendar_event_delete_request') {
                                        logger.info('RAG DELETE', 'Calendar event delete request:', parsed);
                                        withMetadata('calendar_event_delete_request', () => {
                                            metadata.calendar_event_delete_request = parsed;
                                        });
                                        continue;
                                    }

                                    // delete_book asked for confirmation — attach the request
                                    // so the message renders a book-deletion card (ticket count
                                    // + net balance + Confirm/Cancel) before anything is deleted
                                    // (Book Elimination #71).
                                    if (parsed.type === 'book_delete_request') {
                                        logger.info('RAG DELETE', 'Book delete request:', parsed);
                                        withMetadata('book_delete_request', () => {
                                            metadata.book_delete_request = parsed;
                                        });
                                        continue;
                                    }

                                    // rename_book asked for confirmation — attach the request so
                                    // the message renders the current → new name card (#464).
                                    if (parsed.type === 'book_rename_request') {
                                        logger.info('RAG BOOK', 'Book rename request:', parsed);
                                        withMetadata('book_rename_request', () => {
                                            metadata.book_rename_request = parsed;
                                        });
                                        continue;
                                    }

                                    // edit_rulebook asked for confirmation — attach the request
                                    // so the message renders the rule add/update/delete card (#503).
                                    if (parsed.type === 'rule_change_request') {
                                        logger.info('RAG RULEBOOK', 'Rule change request:', parsed);
                                        withMetadata('rule_change_request', () => {
                                            metadata.rule_change_request = parsed;
                                        });
                                        continue;
                                    }

                                    // A rule was added/changed/removed (typed-yes edit_rulebook,
                                    // #503) — an open Rulebook panel refreshes (it listens here).
                                    if (parsed.type === 'rulebook_updated') {
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:rulebook-updated', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // delete_expenses asked for confirmation — attach the
                                    // request so the message renders a batch review card
                                    // (Multi Ticket Deletion #73).
                                    if (parsed.type === 'expense_delete_request') {
                                        logger.info('RAG DELETE', 'Expense delete request:', parsed);
                                        withMetadata('expense_delete_request', () => {
                                            metadata.expense_delete_request = parsed;
                                        });
                                        continue;
                                    }

                                    // A book was permanently deleted — refresh expense views.
                                    if (parsed.type === 'book_deleted') {
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:expense-updated', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // A document was permanently deleted — refresh the file
                                    // manager so it disappears.
                                    if (parsed.type === 'document_deleted') {
                                        logger.info('RAG DELETE', 'Document deleted:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:document-deleted', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // create_contact_from_text asked for confirmation —
                                    // attach the request so the message renders a Confirm /
                                    // Decline / Edit card. The contact is created only when
                                    // the user clicks Confirm (Main chat contact creation).
                                    // #515 Multiple contacts in one message: one turn can
                                    // propose MANY contacts (one tool call per person) —
                                    // accumulate them in the `contact_creation_requests`
                                    // ARRAY so every contact renders its own card (the single
                                    // field was overwritten by the last one). The scalar is
                                    // kept = the FIRST card for older readers.
                                    if (parsed.type === 'contact_creation_request') {
                                        logger.info('RAG CONTACT', 'Contact creation request:', parsed);
                                        withMetadata('contact_creation_request', () => {
                                            (metadata.contact_creation_requests = metadata.contact_creation_requests || []).push(parsed);
                                            if (!metadata.contact_creation_request) metadata.contact_creation_request = parsed;
                                        });
                                        continue;
                                    }

                                    // Teams (Agenda TO Contacts — Phase C / T5): the create/share/
                                    // delete tools ask for confirmation — attach the request so the
                                    // message renders the corresponding ResolutionCard before anything
                                    // mutates. The team is created / shared / deleted only on Confirm.
                                    if (parsed.type === 'team_create_request'
                                        || parsed.type === 'team_share_request'
                                        || parsed.type === 'team_delete_request') {
                                        logger.info('RAG TEAM', parsed.type, parsed);
                                        withMetadata(parsed.type, () => {
                                            metadata[parsed.type] = parsed;
                                        });
                                        continue;
                                    }

                                    // Main Chat invite: invite_to_event resolved network
                                    // contacts + teams and asked for confirmation — attach the
                                    // request so the message renders the event-invite ResolutionCard.
                                    // The event is created + invitations sent only on Confirm.
                                    if (parsed.type === 'event_invite_request') {
                                        logger.info('RAG INVITE', 'Event invite request:', parsed);
                                        // #536: several invite cards can come in one turn
                                        // (≤5 events with invitees) — accumulate them in the
                                        // `event_invite_requests` ARRAY (scalar = the FIRST).
                                        withMetadata('event_invite_request', () => {
                                            (metadata.event_invite_requests = metadata.event_invite_requests || []).push(parsed);
                                            if (!metadata.event_invite_request) metadata.event_invite_request = parsed;
                                        });
                                        continue;
                                    }

                                    // PAI-to-PAI direct send (Agenda TO Contacts — Phase D /
                                    // T6 + T4): send_document_to_contact / send_contact_card asked
                                    // for confirmation — attach the request so the message renders
                                    // the direct-send ResolutionCard. Nothing is sent until Confirm.
                                    if (parsed.type === 'direct_send_request') {
                                        logger.info('RAG SEND', 'Direct send request:', parsed);
                                        withMetadata('direct_send_request', () => {
                                            metadata.direct_send_request = parsed;
                                        });
                                        continue;
                                    }

                                    // A team was created/updated/deleted or a membership/share
                                    // changed via a direct (typed-yes / non-card) chat tool — refresh
                                    // the AgendaPanel's TEAMS tier live.
                                    if (typeof parsed.type === 'string' && parsed.type.startsWith('team_')
                                        && !parsed.type.endsWith('_request')) {
                                        logger.info('RAG TEAM', parsed.type, parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:team-changed', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // Handle calendar event creation events.
                                    // Calendar Ui #226: a single turn can create MANY
                                    // events (the BE emits one calendar_event_created per
                                    // create_calendar_event tool call). Accumulate them in
                                    // an ARRAY so every event renders its own booking card —
                                    // the old single field was overwritten by the last event,
                                    // hiding all the rest (same bug class as Extra Documents
                                    // #187). The scalar `calendar_event_created` is kept
                                    // (= the FIRST event) for back-compat with older
                                    // persisted single-event messages.
                                    // #533: more than 5 different events → ONE package card
                                    // (numbered lines, one Confirm / Cancel). Scalar slot; the
                                    // calendar panel refetches (its pending rows exist now).
                                    if (parsed.type === 'calendar_event_package') {
                                        logger.info('RAG CALENDAR', 'Calendar event package:', parsed);
                                        withMetadata('calendar_event_package', () => {
                                            metadata.calendar_event_package = parsed;
                                        });
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated', { detail: { package_id: parsed.package_id } }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // #536 (PO 2026-11-02): the GUEST answered some events of a
                                    // numbered invitation by text — the updated list (scalar slot).
                                    if (parsed.type === 'event_invitation_package') {
                                        withMetadata('event_invitation_package', () => {
                                            metadata.event_invitation_package = parsed;
                                        });
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated', { detail: { invitation_id: parsed.invitation_id } }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // #545 — IMMEDIATE: this turn's new card replaced the request's
                                    // earlier open card(s). Not message data (never attached to
                                    // metadata): MainOfficeView patches the earlier messages' cards
                                    // closed (utils/cardResolutionPatch.patchSupersededCards).
                                    if (parsed.type === 'cards_superseded') {
                                        logger.info('RAG CARDS', 'Cards superseded:', parsed.action_ids);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:cards-superseded', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    if (parsed.type === 'calendar_event_created') {
                                        logger.info('RAG CALENDAR', 'Calendar event created:', parsed);
                                        withMetadata('calendar_event_created', () => {
                                            (metadata.calendar_events_created = metadata.calendar_events_created || []).push(parsed);
                                            if (!metadata.calendar_event_created) metadata.calendar_event_created = parsed;
                                        });
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:calendar-event-created', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // An existing event was patched (e.g. a "Time to be Confirmed"
                                    // event got a real time) — refresh the calendar panel.
                                    if (parsed.type === 'calendar_event_updated') {
                                        logger.info('RAG CALENDAR', 'Calendar event updated:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // An event was deleted via chat — refresh the calendar panel.
                                    if (parsed.type === 'calendar_event_deleted') {
                                        logger.info('RAG CALENDAR', 'Calendar event deleted:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // Handle reminder creation events — notify any dropdown listening
                                    if (parsed.type === 'reminder_created') {
                                        logger.info('RAG REMINDER', 'Reminder created:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:reminder-created', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }
                                    // Handle reminder edit events — the bell refetches so an
                                    // edited reminder reflects live (mirrors reminder_created).
                                    if (parsed.type === 'reminder_updated') {
                                        logger.info('RAG REMINDER', 'Reminder updated:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:reminder-updated', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }
                                    // Handle reminder delete events — the bell drops the removed
                                    // reminder(s) live (single or bulk).
                                    if (parsed.type === 'reminder_deleted') {
                                        logger.info('RAG REMINDER', 'Reminder deleted:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:reminder-deleted', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // ── Notes engine (Notes & Reminders, Fase 1) ──────────
                                    // IMMEDIATE events — a note was created/updated/deleted via
                                    // the create_note/update_note/delete_notes tools. Dispatch
                                    // window events so the Notes panel refetches live.
                                    if (parsed.type === 'note_created') {
                                        logger.info('RAG NOTE', 'Note created:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:note-created', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }
                                    if (parsed.type === 'note_updated') {
                                        logger.info('RAG NOTE', 'Note updated:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:note-updated', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }
                                    if (parsed.type === 'note_deleted') {
                                        logger.info('RAG NOTE', 'Note deleted:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:note-deleted', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // ── Task List (#437) ──────────────────────────────────
                                    // IMMEDIATE events — create_tasks / update_tasks /
                                    // delete_tasks changed the list. The open Task List
                                    // column refetches on these.
                                    if (parsed.type === 'task_created'
                                        || parsed.type === 'task_updated'
                                        || parsed.type === 'task_deleted') {
                                        logger.info('RAG TASK', 'Task list changed:', parsed);
                                        const evt = `omnis:${parsed.type.replace('_', '-')}`;
                                        try {
                                            window.dispatchEvent(new CustomEvent(evt, { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // ── PAI folder movement (#256) ────────────────────────
                                    // IMMEDIATE event — the move_document tool re-filed a
                                    // document (an explicit "save this to my Legal folder"
                                    // overriding the channel's default upload folder). One
                                    // event per file; an open File Manager listens and
                                    // reloads, so the Bunker updates with no manual refresh.
                                    if (parsed.type === 'document_moved') {
                                        logger.info('RAG MOVE', 'Document moved:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:document-moved', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }
                                    // DEFERRED cards — delete_notes / delete_reminders asked for
                                    // confirmation. Attach the request so the message renders a
                                    // review card (title + snippet / local time + Confirm/Cancel)
                                    // BEFORE anything is deleted.
                                    if (parsed.type === 'note_delete_request') {
                                        logger.info('RAG NOTE', 'Note delete request:', parsed);
                                        withMetadata('note_delete_request', () => {
                                            metadata.note_delete_request = parsed;
                                        });
                                        continue;
                                    }
                                    if (parsed.type === 'task_delete_request') {
                                        logger.info('RAG TASK', 'Task delete request:', parsed);
                                        withMetadata('task_delete_request', () => {
                                            metadata.task_delete_request = parsed;
                                        });
                                        continue;
                                    }
                                    if (parsed.type === 'reminder_delete_request') {
                                        logger.info('RAG REMINDER', 'Reminder delete request:', parsed);
                                        withMetadata('reminder_delete_request', () => {
                                            metadata.reminder_delete_request = parsed;
                                        });
                                        continue;
                                    }
                                    // PAI Shared notes (#259) — DEFERRED card: share_note asked for
                                    // the mandatory confirmation. Attach it so the message renders
                                    // the recipient list + the "full editing privileges" warning
                                    // BEFORE anyone gains access.
                                    if (parsed.type === 'note_share_request') {
                                        logger.info('RAG NOTE', 'Note share request:', parsed);
                                        withMetadata('note_share_request', () => {
                                            metadata.note_share_request = parsed;
                                        });
                                        continue;
                                    }
                                    // Double tap W/T #210 — DEFERRED card: revoke_action asked
                                    // for confirmation before UNDOING an earlier action. Attach the
                                    // request so the message renders the card (label + the SPECIFIC
                                    // entity + Confirm/Cancel) BEFORE anything is removed.
                                    if (parsed.type === 'action_revoke_request') {
                                        logger.info('RAG UNDO', 'Action revoke request:', parsed);
                                        withMetadata('action_revoke_request', () => {
                                            metadata.action_revoke_request = parsed;
                                        });
                                        continue;
                                    }
                                    // IMMEDIATE — an action was undone (typed-yes path, where the BE
                                    // performs the revocation rather than the card). Broadcast so an
                                    // open panel drops the removed row live.
                                    if (parsed.type === 'action_revoked') {
                                        logger.info('RAG UNDO', 'Action revoked:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:action-revoked', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }
                                    // Comment section PAI #242 — DEFERRED card: delete_ticket_comments
                                    // asked for confirmation. Attach the request so the message renders
                                    // a review card (author + snippet + Confirm/Cancel) BEFORE deleting.
                                    if (parsed.type === 'comment_delete_request') {
                                        logger.info('RAG BOARD', 'Comment delete request:', parsed);
                                        withMetadata('comment_delete_request', () => {
                                            metadata.comment_delete_request = parsed;
                                        });
                                        continue;
                                    }
                                    // IMMEDIATE: a comment was deleted via the typed-yes path — refresh
                                    // any open board so the ticket's comment count / thread updates.
                                    if (parsed.type === 'comment_deleted') {
                                        logger.info('RAG BOARD', 'Comment deleted:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:ticket-updated', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }

                                    // Handle expense creation/update events — notify the expenses page
                                    if (parsed.type === 'expense_created') {
                                        logger.info('RAG EXPENSE', 'Expense created:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:expense-created', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }
                                    if (parsed.type === 'expense_updated') {
                                        logger.info('RAG EXPENSE', 'Expense updated:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:expense-updated', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }
                                    if (parsed.type === 'expense_deleted') {
                                        logger.info('RAG EXPENSE', 'Expense deleted:', parsed);
                                        try {
                                            window.dispatchEvent(new CustomEvent('omnis:expense-deleted', { detail: parsed }));
                                            // ExpensesPanel + CalendarPanel listen on omnis:expense-updated for refreshes.
                                            window.dispatchEvent(new CustomEvent('omnis:expense-updated', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }
                                    // list_expenses tool fanout → render interactive cards in chat
                                    if (parsed.type === 'expenses_listed') {
                                        logger.info('RAG EXPENSES_LISTED', parsed);
                                        withMetadata('expenses_listed', () => {
                                            metadata.expenses_listed = parsed;
                                        });
                                        continue;
                                    }
                                    // Kanban boards — chat-driven board/ticket CRUD fanout.
                                    // BoardsPanel listens to all of these for live refresh.
                                    if (parsed.type === 'board_created'
                                        || parsed.type === 'board_updated'
                                        || parsed.type === 'board_deleted'
                                        || parsed.type === 'ticket_created'
                                        || parsed.type === 'ticket_updated'
                                        || parsed.type === 'ticket_deleted'
                                        || parsed.type === 'comment_added'
                                        || parsed.type === 'ticket_attachment_added'
                                        || parsed.type === 'ticket_attachment_removed') {
                                        logger.info('RAG BOARD', parsed.type, parsed);
                                        try {
                                            const evtName = `omnis:${parsed.type.replace(/_/g, '-')}`;
                                            window.dispatchEvent(new CustomEvent(evtName, { detail: parsed }));
                                            // Generic refresh hook for any board-aware panel.
                                            window.dispatchEvent(new CustomEvent('omnis:board-changed', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }
                                    // Atomic Agenda — chat-driven contact CRUD fanout
                                    if (parsed.type === 'contact_created'
                                        || parsed.type === 'contact_updated'
                                        || parsed.type === 'contact_deleted'
                                        || parsed.type === 'contact_note_added') {
                                        logger.info('RAG CONTACT', parsed.type, parsed);
                                        try {
                                            const evtName = `omnis:${parsed.type.replace(/_/g, '-')}`;
                                            window.dispatchEvent(new CustomEvent(evtName, { detail: parsed }));
                                            // Generic refresh hook for the AgendaPanel — it just
                                            // re-fetches the visible list / current contact.
                                            window.dispatchEvent(new CustomEvent('omnis:contact-changed', { detail: parsed }));
                                        } catch (_) { /* no-op */ }
                                        continue;
                                    }
                                    // create_expense's duplicate gate fires this so Message.jsx
                                    // can render the side-by-side comparison card.
                                    if (parsed.type === 'duplicate_warning') {
                                        logger.info('RAG DUPLICATE_WARNING', parsed);
                                        withMetadata('duplicate_warning', () => {
                                            metadata.duplicate_warning = parsed;
                                        });
                                        continue;
                                    }
                                    // create_expense_batch's intra-batch dedup fires this so
                                    // Message.jsx can render the "Eliminate Duplicates /
                                    // Add Regardless" decision card.
                                    if (parsed.type === 'batch_duplicate_summary') {
                                        logger.info('RAG BATCH_DUPLICATE_SUMMARY', parsed);
                                        withMetadata('batch_duplicate_summary', () => {
                                            metadata.batch_duplicate_summary = parsed;
                                        });
                                        continue;
                                    }
                                    // create_expense* fires this when OCR can't read a
                                    // receipt — Message.jsx renders the "We were unable to
                                    // extract data from this image" card WITH the image
                                    // preview, and NO ledger row is created (ticket #57).
                                    if (parsed.type === 'expense_extraction_failed') {
                                        logger.info('RAG EXPENSE_EXTRACTION_FAILED', parsed);
                                        withMetadata('expense_extraction_failed', () => {
                                            metadata.expense_extraction_failed = parsed;
                                        });
                                        continue;
                                    }

                                    // Extract metadata from first chunk
                                    if (!metadata && parsed.has_rag_context !== undefined) {
                                        metadata = {
                                            has_rag_context: parsed.has_rag_context,
                                            sources: parsed.sources || [],
                                            context_chunks_used: parsed.context_chunks_used || 0,
                                            documents_used: parsed.documents_used || [],
                                            documents_used_count: parsed.documents_used_count || 0,
                                            document_ids_used: parsed.document_ids_used || [],
                                            model_used: parsed.model,
                                            full_prompt: parsed.full_prompt,
                                            prompt_breakdown: parsed.prompt_breakdown || null,
                                            pipeline: parsed.pipeline || null,
                                            document_edit: parsed.document_edit || null,
                                            document_create: parsed.document_create || null,
                                            grounding_enabled: parsed.grounding_enabled || false,
                                            grounding_used: false,
                                            grounding_search_count: 0,
                                            // #446: the run that produced this answer —
                                            // persisted with the assistant message so the
                                            // Energy Receipt can tell it from a stopped run.
                                            run_id: parsed.run_id || runId || null,
                                        };
                                        // F-5.2 #2: replay any deferred card events that raced
                                        // ahead of this metadata chunk so their cards still render.
                                        if (pendingDeferred.length) {
                                            logger.info('RAG QUERY', `Flushing ${pendingDeferred.length} buffered deferred event(s) onto metadata`);
                                            for (const mutate of pendingDeferred) {
                                                try { mutate(); } catch (e) { logger.error('RAG QUERY', 'Error replaying deferred event:', e); }
                                            }
                                            pendingDeferred.length = 0;
                                        }
                                    }

                                    // #439: an error chunk is NOT answer text — record it
                                    // (kind/type/ref) and skip the token path.
                                    if (parsed.error && parsed.choices?.[0]?.finish_reason === 'error') {
                                        streamError = {
                                            kind: parsed.error_kind
                                                || (parsed.error_type ? 'functional' : 'technical'),
                                            type: parsed.error_type || null,
                                            message: typeof parsed.error === 'string' ? parsed.error : null,
                                            ref: parsed.error_ref || null,
                                        };
                                        sawTerminator = true;
                                        continue;
                                    }

                                    // Process content tokens
                                    if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta && parsed.choices[0].delta.content) {
                                        if (onToken) onToken(parsed.choices[0].delta.content);
                                    }

                                    // Note finish_reason but keep reading until [DONE]
                                    // (post-stream events like document_edit_complete arrive after finish_reason)
                                    if (parsed.choices && parsed.choices[0] && parsed.choices[0].finish_reason) {
                                        logger.info('RAG QUERY', 'Stream received finish_reason:', parsed.choices[0].finish_reason);
                                        // A finish_reason means the model produced a complete answer,
                                        // even if the [DONE] sentinel never arrives (F-5.2 #1).
                                        sawTerminator = true;
                                    }
                                } catch (e) {
                                    logger.error('RAG QUERY', 'Error parsing stream data:', e);
                                }
                            }
                        }
                    }

                    // Release the reader
                    try { await reader.cancel(); } catch (e) { /* ignore */ }
                    if (!stopped) runFinished = true;

                    // If stream ended without explicit completion signal, call onComplete anyway.
                    // F-5.2 #1: distinguish a clean end (we saw a finish_reason chunk but the
                    // [DONE] sentinel was lost) from a genuinely dropped connection (no terminator
                    // at all). In the latter case the accumulated text is TRUNCATED — flag it so
                    // the UI can mark the saved message "(may be incomplete)" instead of passing it
                    // off as a normal, complete answer.
                    if (!completionCalled && !stopped) {
                        if (!sawTerminator) {
                            logger.warn('RAG QUERY', 'Stream ended WITHOUT [DONE] or finish_reason — response likely truncated (connection dropped)');
                            metadata = { ...(metadata || {}), truncated: true };
                        } else {
                            logger.info('RAG QUERY', 'Stream ended without [DONE] but a finish_reason was seen — treating as complete');
                        }
                        if (onComplete) onComplete(withStreamError(metadata));
                    }
                } catch (error) {
                    // Try to release reader on error too
                    try { await reader.cancel(); } catch (e) { /* ignore */ }
                    if (!stopped && onError) {
                        onError(error);
                    }
                }
            };

            // Start processing
            processStream();

            return stopFunction;

        } catch (error) {
            // Handle abort gracefully
            if (error.name === 'AbortError') {
                logger.info('RAG QUERY', 'Request aborted by user');
                return () => {};
            }
            logger.error('RAG QUERY', 'RAG stream error:', error);
            if (onError) onError(error);
            return () => {};
        }
    },

    /**
     * List user's documents
     * @param {object} options - List options
     * @returns {Promise} Document list
     */
    // Hash-dedup lookup for ticket attachments (#117): returns the existing latest-version
    // document with this SHA-256 content hash, or null.
    async findDocumentByHash(hash) {
        const resp = await apiService.get(`/api/documents/by-hash?hash=${encodeURIComponent(hash)}`);
        return resp?.document || null;
    },

    async listDocuments(options = {}) {
        const params = new URLSearchParams({
            page: options.page || 1,
            per_page: options.per_page || 100
        });

        // Add search parameter if provided
        if (options.search) {
            params.append('search', options.search);
        }

        return apiService.get(`/api/documents/list?${params}`);
    },

    /**
     * Delete a document
     * @param {string} documentId - Document ID
     * @returns {Promise} Delete response
     */
    async deleteDocument(documentId) {
        return apiService.delete(`/api/documents/${documentId}`);
    },

    // Recently Deleted (#126/#127) — soft-delete trash. Move to trash, restore,
    // and list trashed docs (passing the retention policy so the BE auto-purges
    // anything past its retention window before returning the list).
    async trashDocument(documentId) {
        return apiService.post(`/api/documents/${documentId}/trash`, {});
    },

    async restoreDocument(documentId) {
        return apiService.post(`/api/documents/${documentId}/restore`, {});
    },

    async listTrash(retentionDays) {
        const qs = (retentionDays !== undefined && retentionDays !== null && retentionDays !== '')
            ? `?retention_days=${retentionDays}` : '';
        return apiService.get(`/api/documents/trash${qs}`);
    },

    /**
     * Get document statistics
     * @returns {Promise} Document stats
     */
    async getDocumentStats() {
        return apiService.get('/api/documents/stats');
    },

    async getDocumentVersions(documentId) {
        return apiService.get(`/api/documents/${documentId}/versions`);
    },

    /**
     * Permanently delete ONE specific (non-latest) version of a document to free
     * storage (Version Bin #237). Removes the version row, its chunks, and its GCS
     * blob. The BE refuses the latest/current version (409). Returns the refreshed
     * version list.
     * @param {string} versionId - The version's DocumentUpload id
     * @returns {Promise} { success, deleted_version_id, total_versions, versions }
     */
    async deleteDocumentVersion(versionId) {
        return apiService.delete(`/api/documents/versions/${versionId}`);
    },

    async downloadDocument(documentId, filename) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}/download`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        handleAuthError(response);
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'document';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
    },

    /**
     * Rename a document (update original_filename)
     */
    async renameDocument(documentId, newName) {
        return apiService.patch(`/api/documents/${documentId}`, { original_filename: newName });
    },

    /**
     * Save an assistant message verbatim as a document file (no regeneration).
     * Used by the AI-driven "save this as a PDF" chat command path —
     * creates a DocumentUpload row + GCS upload so the file lands in the
     * file manager and can be referenced later.
     */
    async saveMessageAsDocument(messageId, filename) {
        return apiService.post(`/api/documents/from-message/${messageId}`, { filename });
    },

    /**
     * Render an assistant message into a file and trigger a browser
     * download — does NOT persist a copy in the file manager. Used by the
     * explicit "Download as document" overflow menu on chat bubbles.
     */
    async downloadMessageAsDocument(messageId, filename) {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/documents/render-from-message/${messageId}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ filename }),
        });
        handleAuthError(response);
        if (!response.ok) {
            const errData = await response.clone().json().catch(() => null);
            throw httpError(response.status, errData);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Defer revoke a tick so Safari/Firefox actually start the download.
        setTimeout(() => { try { window.URL.revokeObjectURL(url); } catch (_) { /* ignore */ } }, 1000);
    },

    // ============ Folder Methods ============

    /**
     * Get contents of a folder (or root if no folderId)
     * @param {string|null} folderId - Folder ID or null for root
     * @returns {Promise} Folder contents with breadcrumb
     */
    async getFolderContents(folderId = null, search = null) {
        const params = new URLSearchParams();
        if (folderId) params.append('folder_id', folderId);
        if (search) params.append('search', search);
        const queryString = params.toString();
        return apiService.get(`/api/folders/contents${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get EVERY folder the user owns as one flat, path-annotated list.
     *
     * Move folders (#255): the Move dialog needs the whole tree as destinations,
     * and getFolderContents only returns one folder's direct children — so a
     * picker built on it would cost one request per node. This is the same data
     * in a single call; each row is
     * `{id, name, parent_id, folder_kind, is_protected, path, depth}`.
     * @returns {Promise<{folders: Array, count: number}>}
     */
    async getFolderTree() {
        return apiService.get('/api/folders/tree');
    },

    /**
     * Create a new folder
     * @param {string} name - Folder name
     * @param {string|null} parentId - Parent folder ID or null for root
     * @returns {Promise} Created folder
     */
    async createFolder(name, parentId = null) {
        return apiService.post('/api/folders', { name, parent_id: parentId });
    },

    /**
     * Rename a folder
     * @param {string} folderId - Folder ID
     * @param {string} newName - New folder name
     * @returns {Promise} Updated folder
     */
    async renameFolder(folderId, newName) {
        return apiService.patch(`/api/folders/${folderId}`, { name: newName });
    },

    /**
     * Move a folder to a new parent
     * @param {string} folderId - Folder ID
     * @param {string|null} newParentId - New parent folder ID or null for root
     * @returns {Promise} Updated folder
     */
    async moveFolder(folderId, newParentId) {
        return apiService.patch(`/api/folders/${folderId}`, { parent_id: newParentId });
    },

    /**
     * Get all contents of a folder recursively (for deletion progress)
     * @param {string} folderId - Folder ID
     * @returns {Promise} Recursive contents with documents and subfolders
     */
    async getFolderContentsRecursive(folderId) {
        return apiService.get(`/api/folders/${folderId}/contents-recursive`);
    },

    /**
     * Delete a folder
     * @param {string} folderId - Folder ID
     * @param {string} mode - Delete mode: 'cascade', 'move_to_parent', 'fail_if_not_empty'
     * @returns {Promise} Delete response
     */
    async deleteFolder(folderId, mode = 'cascade') {
        return apiService.delete(`/api/folders/${folderId}?mode=${mode}`);
    },

    /**
     * Move multiple items (folders and/or documents) to a target folder
     * @param {string[]} folderIds - Folder IDs to move
     * @param {string[]} documentIds - Document IDs to move
     * @param {string|null} targetFolderId - Target folder ID or null for root
     * @returns {Promise} Move response
     */
    async moveItems(folderIds, documentIds, targetFolderId) {
        return apiService.post('/api/folders/move', {
            folder_ids: folderIds,
            document_ids: documentIds,
            target_folder_id: targetFolderId
        });
    },

    /**
     * Upload a document to a specific folder
     * @param {File} file - The file to upload
     * @param {string|null} folderId - Target folder ID or null for root
     * @param {function} onProgress - Optional progress callback for archives
     * @param {function} onByteProgress - Optional body-progress callback
     * @param {AbortSignal} signal - Optional AbortSignal to cancel a hung upload
     * @returns {Promise} Upload response
     */
    /**
     * @param {string|null} conversationId  #404 — the conversation this file is
     *   being attached to, when there is one. It is what lets the OCR of an
     *   attached image land on that turn's Energy Receipt instead of nowhere:
     *   before it, every `ocr` usage row ever written carried no conversation,
     *   so image processing was charged and debited but could not be shown.
     *   Optional and additive — a File Manager upload has no conversation in
     *   scope and correctly sends none. The server VERIFIES it against the
     *   caller's own conversations and silently ignores anything else, so a
     *   stale id degrades to today's behaviour rather than mis-attributing.
     */
    async uploadDocumentToFolder(file, folderId = null, onProgress = null, onByteProgress = null, signal = null, conversationId = null) {
        const formData = new FormData();
        formData.append('file', file);
        if (folderId) {
            formData.append('folder_id', folderId);
        }
        // '__new__' is the composer's placeholder for a conversation that does
        // not exist yet — never a real id, so never sent.
        if (conversationId && conversationId !== '__new__') {
            formData.append('conversation_id', conversationId);
        }

        try {
            const result = await xhrUpload(`${API_BASE_URL}/api/documents/upload`, formData, {
                onByteProgress,
                signal,
            });

            // Handle archive extraction - upload files one by one
            if (result.type === 'archive_extracted' && onProgress) {
                return await this._uploadExtractedFilesToFolder(result, folderId, onProgress);
            }

            return result;
        } catch (error) {
            console.error('Document upload error:', error);
            throw error;
        }
    },

    /**
     * #513 — unpack a ZIP attached in the Main Chat ("+" / drag-and-drop) on the
     * SERVER into `Conversation Uploads/<zip name>/…` (the File Manager's #494
     * member engine: repeated names auto-renamed, one broken file never stops
     * the rest). Resolves `{type: 'archive_imported', descriptor, summary}` —
     * `descriptor` (no `id`, an `archive_import` block) rides the message's
     * `attached_documents` like a file. A refused archive (password, zip bomb,
     * too large, invalid, 7z) rejects with the server's functional sentence
     * (`err.status` 4xx, `err.code` 'archive_rejected').
     */
    async uploadChatArchive(file, conversationId = null, signal = null, onByteProgress = null) {
        const formData = new FormData();
        formData.append('file', file);
        if (conversationId && conversationId !== '__new__') {
            formData.append('conversation_id', conversationId);
        }
        return xhrUpload(`${API_BASE_URL}/api/documents/chat-archive`, formData, {
            onByteProgress,
            signal,
        });
    },

    /**
     * Get or create a system folder (e.g. 'conversation-uploads', 'generated-by-omnis').
     * Returns the folder ID.
     */
    async ensureSystemFolder(folderKey) {
        const response = await fetch(`${API_BASE_URL}/api/folders/system/${folderKey}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json'
            }
        });
        if (!response.ok) throw new Error(`Failed to ensure system folder: ${folderKey}`);
        const data = await response.json();
        return data.id;
    },

    /**
     * Upload extracted files from archive to a folder (folderId null = root).
     *
     * #494: one member that fails no longer aborts the rest of the archive (a
     * financial export whose `expenses.xlsx` 409'd used to drop every receipt
     * after it, behind a generic "unable to process"). Each failure keeps the
     * server's own sentence; the result lists them in `failed`, and an archive
     * where NOTHING imported throws the first one.
     * @private
     */
    async _uploadExtractedFilesToFolder(extractionResult, folderId, onProgress) {
        const { session_id, archive_name, total_files, files } = extractionResult;
        const uploadedDocuments = [];
        const failed = [];

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                if (onProgress) {
                    onProgress({
                        type: 'progress',
                        current: i + 1,
                        total: total_files,
                        filename: file.filename
                    });
                }

                const formData = new FormData();
                formData.append('session_id', session_id);
                formData.append('file_index', i.toString());
                if (folderId) {
                    formData.append('folder_id', folderId);
                }

                try {
                    const response = await fetch(`${API_BASE_URL}/api/documents/upload-from-session`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${getAuthToken()}`
                        },
                        body: formData
                    });
                    const body = await response.json().catch(() => null);
                    if (!response.ok) {
                        const err = new Error(body?.error?.message || body?.message || `Failed to upload ${file.filename}`);
                        err.status = response.status;
                        err.code = body?.error?.type;
                        throw err;
                    }
                    uploadedDocuments.push(body.document);
                } catch (memberError) {
                    failed.push({ filename: file.filename, error: memberError });
                }
            }

            // Clean up session
            await fetch(`${API_BASE_URL}/api/documents/cleanup-session`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getAuthToken()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ session_id })
            });

            if (failed.length && !uploadedDocuments.length) {
                throw failed[0].error;
            }

            return {
                type: 'archive',
                archive_name: archive_name,
                total_documents: uploadedDocuments.length,
                documents: uploadedDocuments,
                failed,
            };
        } catch (error) {
            try {
                await fetch(`${API_BASE_URL}/api/documents/cleanup-session`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${getAuthToken()}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ session_id })
                });
            } catch (cleanupError) {
                console.error('Cleanup error:', cleanupError);
            }
            throw error;
        }
    },

    /**
     * Upload an audio blob for transcription (voice messages).
     * @param {Blob} audioBlob - The recorded audio blob
     * @param {string|null} conversationId - The conversation this clip belongs
     *   to, so its transcription cost lands on the Energy Receipt's
     *   Transcription line instead of being written with no conversation at all
     *   (#377 — that is why the line read zero for every voice message).
     *   Null/absent is legitimate: a brand-new chat has no id yet at record
     *   time, and the receipt claims that orphaned row back by time window.
     *   The server VERIFIES ownership and ignores an id the caller doesn't own.
     * @returns {Promise<{transcription: string, audio_url: string, gcs_path: string, mime_type: string}>}
     */
    async uploadAndTranscribeAudio(audioBlob, conversationId = null) {
        const formData = new FormData();
        formData.append('file', audioBlob, `voice_${Date.now()}.webm`);
        if (conversationId) formData.append('conversation_id', conversationId);

        const response = await fetch(`${API_BASE_URL}/api/audio/transcribe`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: formData,
        });

        handleAuthError(response);

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw httpError(response.status, errorData);
        }

        return response.json();
    },

    /**
     * Dictation (#541): transcribe a clip for the Main Chat composer. Same
     * endpoint as voice notes, with `mode=dictation` — the backend keeps no
     * audio and answers with the text only, in ONE round-trip (the text must
     * reach the input ~1 s after the user taps stop).
     * @param {Blob} audioBlob - The recorded webm clip
     * @param {string|null} conversationId - Origin conversation (Energy Receipt
     *   attribution); null for a brand-new chat.
     * @returns {Promise<{transcription: string, mode: 'dictation', empty: boolean}>}
     */
    async dictate(audioBlob, conversationId = null) {
        const formData = new FormData();
        formData.append('file', audioBlob, `dictation_${Date.now()}.webm`);
        if (conversationId) formData.append('conversation_id', conversationId);
        formData.append('mode', 'dictation');

        const response = await fetch(`${API_BASE_URL}/api/audio/transcribe`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${getAuthToken()}`
            },
            body: formData,
        });

        handleAuthError(response);

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw httpError(response.status, errorData);
        }

        const data = await response.json();
        const transcription = typeof data?.transcription === 'string' ? data.transcription.trim() : '';
        return {
            transcription,
            mode: 'dictation',
            empty: Boolean(data?.empty) || transcription.length === 0,
        };
    },

    /**
     * Build the audio stream URL for a voice message.
     * Uses the backend proxy (no signed URLs needed).
     * @param {string} audioUrl - The relative audio URL from the transcribe response
     * @returns {string} Full URL for audio playback
     */
    getAudioStreamUrl(audioUrl) {
        if (!audioUrl) return null;
        // If already absolute, return as-is
        if (audioUrl.startsWith('http')) return audioUrl;
        // Build full URL from relative path
        return `${API_BASE_URL}${audioUrl}`;
    },

    /**
     * Mint a fresh signed playback URL for a stored voice message.
     * Called when an <audio> element errors on an expired (or legacy
     * ?user_id=) stream URL — the BE re-signs it for the owner (H-0.1).
     * @param {string} gcsPath - The stored gs:// path of the voice clip
     * @returns {Promise<string|null>} Absolute playback URL, or null
     */
    async refreshAudioUrl(gcsPath) {
        if (!gcsPath) return null;
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/audio/url?gcs_path=${encodeURIComponent(gcsPath)}`,
                { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
            );
            if (!response.ok) return null;
            const data = await response.json();
            return this.getAudioStreamUrl(data.audio_url);
        } catch (_) {
            return null;
        }
    }
};
